import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { Field } from 'formik';
import { FieldLayoutTwoColumn } from 'cstar-react-primitives';
import { InputGroup, SelectGroup } from 'cstar-react-primitives/lib/formik/index';

import { useFormatMessage } from '../../intl/intlHooks';
import { addressFieldNames, FIRST_NAME_MAX_LENGTH, LAST_NAME_MAX_LENGTH, FULL_NAME_MAX_LENGTH, CARD_NAME_MAX_LENGTH } from '../../address';

import { cashbotName } from '../core';

import { formatPhoneNumberUS } from './addressFieldFormatters';
import { stateOptions, addressMessages } from './messages';

export const defaultAddressFieldValues = Object.keys(addressFieldNames).reduce(
    (fieldValues, fieldName) => ({ ...fieldValues, [addressFieldNames[fieldName]]: '' }),
    {}
);

export const EmailFormikField = ({ label, fieldName }) => (
    <Field
        name={fieldName}
        label={label}
        component={InputGroup}
        atts={{ type: 'email', maxLength: 254 }}
        cashbotName={cashbotName}
    />
);

EmailFormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_EMAIL
};

EmailFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string
};

export const FullNameFormikField = ({ label, fieldName, maxLength }) => (
    <Field
        name={fieldName}
        label={label}
        component={InputGroup}
        atts={{ maxLength }}
        cashbotName={cashbotName}
    />
);

FullNameFormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_FULLNAME,
    maxLength: FULL_NAME_MAX_LENGTH
};

FullNameFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string,
    maxLength: PropTypes.number
};

export const FirstNameFormikField = ({ label, fieldName }) => (
    <Field
        name={fieldName}
        label={label}
        component={InputGroup}
        atts={{ maxLength: FIRST_NAME_MAX_LENGTH }}
        cashbotName={cashbotName}
    />
);

FirstNameFormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_FIRSTNAME
};

FirstNameFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string
};

export const LastNameFormikField = ({ label, fieldName }) => (
    <Field
        name={fieldName}
        label={label}
        component={InputGroup}
        atts={{ maxLength: LAST_NAME_MAX_LENGTH }}
        cashbotName={cashbotName}
    />
);

LastNameFormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_LASTNAME
};

LastNameFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string
};

export const Street1FormikField = ({ label, fieldName }) => (
    <Field name={fieldName} label={label} component={InputGroup} atts={{ maxLength: 100 }} cashbotName={cashbotName} />
);

Street1FormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_STREET1
};

Street1FormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string
};

export const Street2FormikField = ({ label, fieldName }) => (
    <Field name={fieldName} label={label} component={InputGroup} atts={{ maxLength: 100 }} cashbotName={cashbotName} />
);

Street2FormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_STREET2
};

Street2FormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string
};

export const CityFormikField = ({ label, fieldName }) => (
    <Field name={fieldName} label={label} component={InputGroup} atts={{ maxLength: 50 }} cashbotName={cashbotName} />
);

CityFormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_CITY
};
CityFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string
};

export const StateFormikField = ({ label, fieldName, countryCode = 'US' }) => (
    <Field
        name={fieldName}
        label={label}
        component={SelectGroup}
        options={stateOptions({ formatMessage: useFormatMessage(), countryCode })}
        atts={{ maxLength: 50 }}
        cashbotName={cashbotName}
    />
);

StateFormikField.defaultProps = {
    countryCode: 'US',
    fieldName: addressFieldNames.ADDRESS_STATE
};

StateFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string,
    countryCode: PropTypes.string
};

export const PostalCodeFormikField = ({ label, fieldName }) => (
    <Field
        name={fieldName}
        label={label}
        component={InputGroup}
        atts={{ type: 'tel', maxLength: 10 }}
        cashbotName={cashbotName}
    />
);

PostalCodeFormikField.defaultProps = {
    fieldName: addressFieldNames.ADDRESS_POSTCODE
};

PostalCodeFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string
};

export const PhoneFormikField = ({ label, fieldName, format }) => (
    <Field
        name={fieldName}
        label={label}
        component={InputGroup}
        atts={{ type: 'tel', maxLength: 20 }}
        cashbotName={cashbotName}
        format={format}
    />
);

PhoneFormikField.defaultProps = {
    format: formatPhoneNumberUS,
    fieldName: addressFieldNames.ADDRESS_PHONE
};

PhoneFormikField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldName: PropTypes.string,
    format: PropTypes.func
};

export const AddressForm = ({isSelfBuy, useCardNameField}) => {
    const formatMessage = useFormatMessage();
    return (
        <Fragment>
            <FullNameFormikField
                label={formatMessage(addressMessages.recipientFullNameLabel, {isSelfBuy})}
                maxLength={useCardNameField ? (CARD_NAME_MAX_LENGTH) : (FULL_NAME_MAX_LENGTH)}
            />
            <Street1FormikField label={formatMessage(addressMessages.street1Label)} />
            <Street2FormikField label={formatMessage(addressMessages.street2Label)} />
            <CityFormikField label={formatMessage(addressMessages.cityLabel)} />
            <FieldLayoutTwoColumn>
                <StateFormikField label={formatMessage(addressMessages.stateLabel)} />
                <PostalCodeFormikField label={formatMessage(addressMessages.postalCodeLabel)} />
            </FieldLayoutTwoColumn>
        </Fragment>
    );
};

AddressForm.propTypes = {
    isSelfBuy: PropTypes.bool.isRequired,
    useCardNameField: PropTypes.bool,
};

AddressForm.defaultProps = {
    useCardNameField: false,
};

export const PaymentAddressForm = () => {
    const formatMessage = useFormatMessage();
    return (
        <Fragment>
            <Street1FormikField label={formatMessage(addressMessages.street1Label)} />
            <CityFormikField label={formatMessage(addressMessages.cityLabel)} />
            <FieldLayoutTwoColumn>
                <StateFormikField label={formatMessage(addressMessages.stateLabel)} />
                <PostalCodeFormikField label={formatMessage(addressMessages.postalCodeLabel)} />
            </FieldLayoutTwoColumn>
        </Fragment>
    );
};
